import { cacheActions, cachedResponse, store } from './store';

const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:8080').replace(/\/+$/, '');
const TOKEN_KEY = 'caption_maker_admin_token';
// Keep snapshots for the lifetime of this console session. The explicit
// Refresh control is the invalidation point; a time-based expiry would make
// merely revisiting a navigation item issue another request unexpectedly.
const CACHE_TTL_MS = Number.POSITIVE_INFINITY;
const inflight = new Map();

export const authToken = () => localStorage.getItem(TOKEN_KEY);
export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  store.dispatch(cacheActions.cleared());
};

const parse = async (response) => {
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(body?.error?.message ?? 'The request could not be completed.');
    error.status = response.status;
    error.code = body?.error?.code;
    throw error;
  }
  return body;
};

export const login = async ({ email, password }) => {
  let response = await fetch(`${API_URL}/v1/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (response.status === 404) {
    response = await fetch(`${API_URL}/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  }
  const body = await parse(response);
  if (!body.token) throw new Error('The login service did not return an access token.');
  localStorage.setItem(TOKEN_KEY, body.token);
  store.dispatch(cacheActions.cleared());
  return body;
};

/** Clears cached snapshots and asks the mounted page to refetch — no browser reload. */
export const refreshAdminCache = () => store.dispatch(cacheActions.refreshed());

export const api = async (path, options = {}) => {
  const method = (options.method ?? 'GET').toUpperCase();
  const cacheable = method === 'GET';
  if (cacheable) {
    const cached = cachedResponse(path);
    if (cached) return cached;
    if (inflight.has(path)) return inflight.get(path);
  }

  const token = authToken();
  const headers = new Headers(options.headers ?? {});
  headers.set('Authorization', `Bearer ${token ?? ''}`);
  headers.set('X-Request-Id', crypto.randomUUID());
  if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const request = fetch(`${API_URL}/v1/admin${path}`, { ...options, headers }).then(parse);

  if (!cacheable) {
    const data = await request;
    // A mutation can change every list and metric. Drop stale snapshots, but
    // never start background refetches: the next visible screen decides when.
    store.dispatch(cacheActions.cleared());
    return data;
  }

  inflight.set(path, request);
  try {
    const data = await request;
    store.dispatch(cacheActions.received({ key: path, data, expiresAt: Date.now() + CACHE_TTL_MS }));
    return data;
  } finally {
    inflight.delete(path);
  }
};
